import {
  EXPORT_TYPE_NAMES,
  ExportJobTypes,
  ExportTransportTypes,
  ExportType,
  ExportTypes,
  ReportNames,
} from 'constants/exportTypes';
import { Resources } from 'constants/resources';

import React, { useState } from 'react';

import { useQuery } from '@tanstack/react-query';
import { getUserInfo } from 'config/requests';
import { useReportExport } from 'hooks/queries/useReportExport';
import { useRecordModal } from 'hooks/useRecordModal';
import { useRecordModalById } from 'hooks/useRecordModalById';
import { BankOperationsEditCommentDialog } from 'pages/Finance/BankOperations/BankOperationsEditCommentDialog/BankOperationsEditCommentDialog';
import { useListController, useNotify } from 'react-admin';
import { ExportTypesButton } from 'shared/mui/Buttons/ExportTypesButton/ExportTypesButton';
import { Typography } from 'shared/mui/Typography';
import { ExportEditButton } from 'shared/react-admin/Export/ExportEditButton/ExportEditButton';
import { ListPage } from 'shared/react-admin/Pages/ListPage/ListPage';
import { MerchantAccountShowDialog } from 'shared/widgets/MerchantAccountShowDialog/MerchantAccountShowDialog';
import { ReportExportDialog } from 'shared/widgets/ReportExportDialog/ReportExportDialog';
import { ReportExportErrorDialog } from 'shared/widgets/ReportExportDialog/ReportExportErrorDialog/ReportExportErrorDialog';
import { BankOperation } from 'types/embedded/bankOperation';
import { MerchantAccount } from 'types/embedded/merchantAccount';

import { BankOperationsDatagrid } from './BankOperationsDatagrid/BankOperationsDatagrid';
import { BankOperationsFilter } from './BankOperationsFilter/BankOperationsFilter';
import { BankOperationsListCommentDialog } from './BankOperationsListCommentDialog/BankOperationsListCommentDialog';
import { EXPORT_FIELDS_BANK_OPS } from './constants/exportFields';

export const BankOperationsList: React.FC = () => {
  const {
    filterValues,
    isLoading: isListLoading,
    total,
  } = useListController<BankOperation>({
    resource: Resources.BankOperations,
    sort: { field: 'id', order: 'DESC' },
    queryOptions: { enabled: false },
  });
  const { data: user } = useQuery({ queryKey: ['me'], queryFn: getUserInfo });
  const notify = useNotify();

  const [exportType, setExportType] = useState<ExportType>(ExportTypes.Excel);
  const [isOpenNotifyDialog, setIsOpenNotifyDialog] = useState(false);
  const [isSendReportOnEmail, setIsSendReportOnEmail] = useState(false);
  const [isOpenErrorDialog, setIsOpenErrorDialog] = useState(false);

  const { mutate: exportMutate, isLoading: isExportLoading } = useReportExport({
    filters: {
      ...filterValues,
      exportType,
      jobType: ExportJobTypes.Bank_Operation_Final_Statuses,
      ...(isSendReportOnEmail && { notificationTransport: ExportTransportTypes.Email }),
    },
    preferenceKey: ReportNames.Bank_Operation_Final_Statuses,
    onSuccess: () => {
      notify(
        `Файл экспорта отчёта по "Банковским операциям" формируется ${isSendReportOnEmail ? 'и будет отправлен вам на почту' : ''}`,
        {
          type: 'info',
        },
      );
      setIsOpenNotifyDialog(false);
    },
    onError: (error) => {
      notify(error.data?.errors[0]?.title, { type: 'error' });
    },
  });

  const {
    record: merchantAccount,
    handleOpenRecordModal: handleOpenMerchantAccountModal,
    handleCloseRecordModal: handleCloseMerchantAccountModal,
    openRecordModal: openMerchantAccountModal,
    isFetching: isFetchingMerchantAccount,
  } = useRecordModalById<MerchantAccount>({ resource: Resources.MerchantAccounts });

  const {
    record: recordComment,
    handleOpenRecordModal: handleOpenRecordModalComment,
    openRecordModal: openRecordModalComment,
    handleCloseRecordModal: handleCloseRecordModalComment,
  } = useRecordModal<BankOperation>();

  const {
    record: recordEditComment,
    handleOpenRecordModal: handleOpenRecordModalEditComment,
    openRecordModal: openRecordModalEditComment,
    handleCloseRecordModal: handleCloseRecordModalEditComment,
  } = useRecordModal<BankOperation>();

  const hasDateFilter = Object.keys(filterValues).some((key) =>
    ['dateOfOperation', 'approveAt', 'canceledAt'].some((field) => key.includes(field)),
  );

  return (
    <ListPage
      actions={[
        <ExportTypesButton
          disabled={!total}
          exportType={exportType}
          key="export-button"
          onChangeType={setExportType}
          onClick={() => {
            if (!hasDateFilter) {
              setIsOpenErrorDialog(true);
              return;
            }
            setIsOpenNotifyDialog(true);
          }}
        />,
      ]}
      empty={false}
      headerTitle={{
        titleText: 'Банковские операции',
        tooltipText:
          'Ручная операция, связанная с созданием заявки на движение денежных средств (с направлением на ввод или вывод).',
      }}
      resource={Resources.BankOperations}
      sort={{ field: 'id', order: 'DESC' }}
      sx={{
        '& .MuiPaper-root': {
          overflow: 'auto',
        },
      }}
    >
      <MerchantAccountShowDialog
        isFetching={isFetchingMerchantAccount}
        merchantAccount={merchantAccount}
        onClose={handleCloseMerchantAccountModal}
        open={openMerchantAccountModal}
      />
      <BankOperationsListCommentDialog
        onClose={handleCloseRecordModalComment}
        open={openRecordModalComment}
        record={recordComment}
      />
      <BankOperationsEditCommentDialog
        onClose={handleCloseRecordModalEditComment}
        open={openRecordModalEditComment}
        record={recordEditComment}
      />
      <BankOperationsFilter />
      <BankOperationsDatagrid
        handleOpenMerchantAccountModal={handleOpenMerchantAccountModal}
        handleOpenRecordModalComment={handleOpenRecordModalComment}
        handleOpenRecordModalEditComment={handleOpenRecordModalEditComment}
      />
      <ReportExportErrorDialog
        message={
          <Typography>
            Для экспорта необходимо указать период хотя бы по одной дате:
            <br />
            Дата в ПС / период, Дата аннулирования / период, Дата согласования / период.
          </Typography>
        }
        onClose={() => setIsOpenErrorDialog(false)}
        open={isOpenErrorDialog}
        title="Ошибка экспорта"
      />
      <ReportExportDialog
        exportType={EXPORT_TYPE_NAMES[exportType]}
        isLoading={isListLoading || isExportLoading}
        onChange={() => setIsSendReportOnEmail((prev) => !prev)}
        onClose={() => setIsOpenNotifyDialog(false)}
        onSubmit={exportMutate}
        open={isOpenNotifyDialog}
        sendOnEmail={isSendReportOnEmail}
        title={
          <>
            <Typography component="span" sx={{ verticalAlign: 'middle' }} variant="inherit">
              Экспорт отчёта
            </Typography>
            <ExportEditButton
              fields={EXPORT_FIELDS_BANK_OPS}
              preferenceKey={ReportNames.Bank_Operation_Final_Statuses}
            />
          </>
        }
        userEmail={user?.corporateEmail || user?.email}
      />
    </ListPage>
  );
};
